import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import { registerServiceWorker } from './utils/pwa';
import { purgeLegacyDrafts } from './utils/saveErrorHandler';  // Iter-281 — purge pre-fix drafts
import { ensureFreshRuntimeAtBoot } from './utils/runtimeFreshness';  // Iter-411s — app-boot freshness
import { initOfflineUploadQueue } from './utils/offlineUploadQueue';  // 2026-08-31 — offline photo queue
import { reportFrontendError, isThirdPartyErrorSource } from './utils/errorBeacon';  // 2026-09-04 — global client error capture
import { QueuedPhotosBadge } from './components/QueuedPhotosBadge';
import PWAInstallBanner from './components/PWAInstallBanner';
import NavigationProgress from './components/NavigationProgress';
import ImpersonationBanner from './components/ImpersonationBanner';
import ServerWakingBanner from './components/ServerWakingBanner';  // Iter-292e — P0 deploy stability
import AdminGuidedTour from './components/AdminGuidedTour';
import WhatsNewModal from './components/WhatsNewModal';
import OnboardingWizardGate from './components/OnboardingWizardGate';  // Iter-315m
import AgreementGateModal from './components/AgreementGateModal';
import ErrorBoundary from './components/ErrorBoundary';
import AdminHelp from './pages/admin/Help';
import TalentHelp from './pages/talent/Help';
import ClientHelp from './pages/client/Help';
import BackgroundChecksKanban from './pages/admin/BackgroundChecksKanban';
import EnterpriseApiKeys from './pages/admin/EnterpriseApiKeys';
import BookingRequestsAdmin from './pages/admin/BookingRequestsAdmin';
import PublicBooking from './pages/PublicBooking';
import PublicJobShare from './pages/PublicJobShare';
import PublicCompanyHub from './pages/PublicCompanyHub';
import RecruitingToolkit from './pages/client/RecruitingToolkit';
import MagicLogin from './pages/MagicLogin';
import PayInvoice from './pages/PayInvoice';  // Iter-327 — Universal payment link (guest checkout)
import Marketplace from './pages/Marketplace';  // Iter-334 — Public marketplace
import VendorPublicPage from './pages/VendorPublicPage';  // Iter-334 — Public vendor page
import BookingConfirmation from './pages/BookingConfirmation';  // Iter-334 Phase A — Booking receipt
import VendorDashboardMarketplace from './pages/vendor/VendorDashboardMarketplace';  // Iter-334 Phase A
import AdminMarketplaceVendors from './pages/admin/AdminMarketplaceVendors';  // Iter-334 Phase A
import AdminVendorQueue from './pages/admin/AdminVendorQueue';  // Phase 2 vendor foundation sprint
import AdminVendorMigration from './pages/admin/AdminVendorMigration';  // Phase 2 vendor foundation — Busy Woo migration UI
import ZapierIntegrations from './pages/admin/ZapierIntegrations';  // Zapier Integration Sprint · Phase 1
import VendorPromoAdmin from './pages/admin/VendorPromoAdmin';  // Iter-373 Vendor Launch Promotion admin
import AdminHealthDashboard from './pages/admin/AdminHealthDashboard';  // Iter-339 Production Health
import TalentDbProtection from './pages/admin/TalentDbProtection';  // Critical #1 — Anti-scraping / talent DB protection
import AdminCodeOfConduct from './pages/admin/AdminCodeOfConduct';
import AdminCoCAcceptances from './pages/admin/AdminCoCAcceptances';
import AdminClientConcerns from './pages/admin/AdminClientConcerns';
import AdminDiscrepancies from './pages/admin/AdminDiscrepancies';  // Iter-346 Discrepancy Report
import ShareLinkResolver from './pages/ShareLinkResolver';  // Iter-334 Phase A

// Auth pages
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';  // Social Sign In callback (2026-08-05)
import Register from './pages/Register';
import AdminCompanySignersPage from './pages/admin/AdminCompanySignersPage';
import SmsTermsPage from './pages/SmsTermsPage';
import SmsConsentPage from './pages/SmsConsentPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import DataDeletionPage from './pages/DataDeletionPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Activate from './pages/Activate';   // Iter-262b — closes broken /activate email link
import VerifyContact from './pages/VerifyContact';
import VerifyPhone from './pages/VerifyPhone';   // iter429 — universal SMS phone verification
import { TalentProfileModalProvider } from './components/TalentProfileModalProvider';
import { InviteTalentToShiftProvider } from './components/InviteTalentToShiftProvider';
import { InviteCartProvider } from './components/InviteCartProvider';
import InviteCartFloatingBar from './components/InviteCartFloatingBar';
import UpdateAvailableToast from './components/UpdateAvailableToast';
import AppUpdateManager from './components/AppUpdateManager';
import InviteCartDrawer from './components/InviteCartDrawer';
import SetPassword from './pages/SetPassword';

// Client pages
import ClientDashboard from './pages/client/Dashboard';
import ClientShifts from './pages/client/Shifts';
import CreateShift from './pages/client/CreateShift';
import EvieBooking from './pages/client/EvieBooking';
import ClientCalendar from './pages/client/Calendar';
import CommunityFeed from './pages/CommunityFeed';
import CommunityMemberProfile from './pages/CommunityMemberProfile';
import AdminPermissionsMatrix from './pages/admin/Permissions';
import BillingApprovals from './pages/admin/BillingApprovals';
import CleanupCenter from './pages/admin/CleanupCenter';
import AdminInvoices from './pages/admin/AdminInvoices';
import LiveEventInbox from './pages/admin/LiveEventInbox';  // Phase-1 Live Event Messaging
import AdminStaffPayments from './pages/admin/AdminStaffPayments';  // Iter-328 Phase 1
import AdminExpenses from './pages/admin/AdminExpenses';
import AlertPreferences from './pages/admin/AlertPreferences';
import CompanyUsers from './pages/client/CompanyUsers';
import ClientBilling from './pages/client/Billing';
import AcceptInvite from './pages/AcceptInvite';
import ShiftDetails from './pages/client/ShiftDetails';
import ClientBookings from './pages/client/Bookings';
import ClientFavorites from './pages/client/Favorites';
import ClientCalendarIntegrations from './pages/client/calendar/ClientCalendarIntegrations';
import ClientCalendarReviewQueue from './pages/client/calendar/ClientCalendarReviewQueue';
import ClientCalendarSyncLogs from './pages/client/calendar/ClientCalendarSyncLogs';
import ClientRatings from './pages/client/Ratings';
import ClientProfile from './pages/client/Profile';
import ClientInvoices from './pages/client/Invoices';
import ClientMessages from './pages/client/Messages';
import AdminSupportChat from './pages/client/AdminSupportChat';  // 2026-06 — Admin↔Client messaging
import EvieAssistant from './pages/client/EvieAssistant';  // Phase C — Evie client assistant
import PaymentHoldScreen from './pages/client/PaymentHoldScreen';  // iter433 — on-hold-for-payment restriction screen
import AmbassadorMediaAudit from './pages/admin/AmbassadorMediaAudit';  // Admin — showcase media audit
import EventHistory from './pages/admin/EventHistory';  // Historical event calendar + recovery
import StaffPayments from './pages/admin/StaffPayments';  // Outstanding staff settlements dashboard
import EvieCalls from './pages/admin/EvieCalls';  // Evie AI Telephone Receptionist dashboard
import ClientBookingDefaults from './pages/admin/ClientBookingDefaults';  // Per-client standard booking defaults
import BlockedClients from './pages/talent/BlockedClients';  // Iter-410 talent → client blocking UI
import BlockedTalent from './pages/client/BlockedTalent';        // 2026-06 — Client Block Talent
import ClientChatThread from './pages/client/ChatThread';  // V413 — Client Messaging Audit
import TalentDiscovery from './pages/client/TalentDiscovery';
import TalentProfileDetail from './pages/client/TalentProfileDetail';
import CreatorProfileView from './pages/client/CreatorProfileView';
import VAActionQueue from './pages/admin/VAActionQueue';
import TalentMessagesCenter from './pages/talent/MessagesCenterPage';  // Iter-268
import ClientMessagesCenter from './pages/client/MessagesCenterPage';  // Iter-270
import AdminMessagesCenter from './pages/admin/MessagesCenterPage';    // Iter-270
import RecapDiag from './pages/talent/RecapDiag';  // Iter-270 — voice recap mobile diagnostics
import NotificationPreferencesRouter from './pages/NotificationPreferencesRouter';  // Iter-273 — email footer destination
import NotificationUnsubscribePage from './pages/NotificationUnsubscribePage';  // Iter-273 — email footer destination
import AdminGPSTrail from './pages/admin/AdminGPSTrail';  // Iter-263 — GPS attendance trail replay
import AdminAttendanceQueue from './pages/admin/AdminAttendanceQueue';  // Iter-263 — attendance review queue
import AdminReliabilityDashboard from './pages/admin/AdminReliabilityDashboard';  // Iter-266 — GPS Phase B reliability
import AdminReliabilityBeta from './pages/admin/AdminReliabilityBeta';            // Iter-412a — Admin-only beta validation
import AdminTalentReliability from './pages/admin/AdminTalentReliability';  // Iter-266 — per-talent profile
import AdminComplianceDashboard from './pages/admin/AdminComplianceDashboard';  // PO8/PO9 — Compliance engine + dashboard
import PaymentHoldAdmin from './pages/admin/PaymentHoldAdmin';  // iter433 — payment-hold admin console
import JobOverview from './pages/client/JobOverview';
import AttendanceDashboard from './pages/client/AttendanceDashboard';
import EventRecap from './pages/client/EventRecap';
import ClientReports from './pages/client/ClientReports';
import ClientAgreement from './pages/client/ClientAgreement';
import BookingManagement from './pages/client/BookingManagement';
import ClientSubscription from './pages/client/Subscription';
import CreateEventSeries from './pages/client/CreateEventSeries';

// Talent pages
import TalentDashboard from './pages/talent/Dashboard';
import TalentAvailability from './pages/talent/TalentAvailability';  // Iter-226g
import MyInvitations from './pages/talent/MyInvitations';  // Iter-227a

// Iter-228 — Agency Recruitment
import AgencyRecruitmentPage from './pages/public/AgencyRecruitmentPage';
import AgencyRecruitmentSettings from './pages/agency/RecruitmentSettings';
import AgencyRoster from './pages/agency/Roster';
import TalentShifts from './pages/talent/Shifts';
import MyApplications from './pages/talent/MyApplications';
import TalentBookings from './pages/talent/Bookings';
import TalentBookingsV2 from './pages/talent/BookingsV2';
import BookingsRouter from './pages/talent/BookingsRouter';
import BookingDetailsV2 from './components/booking/BookingDetailsV2';
import TalentSettlements from './pages/talent/Earnings';
import TaxCenter from './pages/talent/TaxCenter';
import TaxAdmin from './pages/admin/TaxAdmin';
import TalentPostEvent from './pages/talent/PostEvent';
// Iter-133s — Phase C role-based post-event survey
import PostEventSurveyV2 from './pages/talent/PostEventSurveyV2';
import ShiftRecapV2 from './pages/shared/ShiftRecapV2';
import PayoutsReturn from './pages/talent/PayoutsReturn';
import IDVerification from './pages/talent/IDVerification';
import NotificationCenter from './pages/talent/NotificationCenter';
import TalentShiftDetail from './pages/talent/ShiftDetail';
import Notifications from './components/Notifications';
import NotFound from './components/NotFound';
import LegacyRedirect from './components/LegacyRedirect';
import TalentRatings from './pages/talent/Ratings';
import TalentHome from './pages/talent/Home';
import TalentMyCard from './pages/talent/MyCard';
import TrainingHub from './pages/talent/TrainingHub';
import TrainingCategory from './pages/talent/TrainingCategory';
import TrainingModule from './pages/talent/TrainingModule';

// Admin pages
import AdminDashboard from './pages/admin/Dashboard';
import AdminAnalytics from './pages/admin/Analytics';
import AdminRevenue from './pages/admin/Revenue';
import AdminShiftMetricsPreview from './pages/admin/ShiftMetricsPreview';
import AdminShiftMetricsReview from './pages/admin/ShiftMetricsReview';
import ReferralLeaderboard from './pages/admin/ReferralLeaderboard';
import AdminUserDetail from './pages/admin/UserDetail';
import AdminMarkets from './pages/admin/Markets';
import AdminHome from './pages/admin/Home';
import AdminHomeRouter from './pages/admin/AdminHomeRouter';
import AdminSmokeTest from './pages/admin/AdminSmokeTest';
import ClientBusinessRulesConsole from './pages/admin/ClientBusinessRulesConsole';
import AdminMessages from './pages/admin/AdminMessages';
import AdminDirectMessage from './pages/admin/AdminDirectMessage';
import DiscoveryMessage from './pages/client/DiscoveryMessage';
import FlaggedMessages from './pages/admin/FlaggedMessages';
import ClientTalentBlocks from './pages/admin/ClientTalentBlocks';  // 2026-06 — Client Block Talent
import FlaggedProfileImages from './pages/admin/FlaggedProfileImages';
import AiEscalations from './pages/admin/AiEscalations';
import ClientTeamMembers from './pages/client/ClientTeamMembers';
// Iter-236 — Campaign Intelligence Phase 2
import EventInsightsDashboard from './pages/insights/EventInsightsDashboard';
import ClientInsightsRollup from './pages/insights/ClientInsightsRollup';
import AdminClientInsights from './pages/insights/AdminClientInsights';
// Iter-237 — Campaign Intelligence Phase 3
import CreatorAffiliateDashboard from './pages/talent/CreatorAffiliateDashboard';
import DistributionLists from './pages/client/DistributionLists';
import ContractManager from './pages/admin/ContractManager';
import AdminProfile from './pages/admin/AdminProfile';
import AdminSettings from './pages/admin/AdminSettings';
import AdminNotificationPreferences from './pages/admin/NotificationPreferences';
import AdminRecapCompliance from './pages/admin/RecapCompliance';
import AdminDeliveryHealth from './pages/admin/DeliveryHealth';
import AdminSettlementChallenges from './pages/admin/SettlementChallenges';
import AdminAttendanceCorrections from './pages/admin/AttendanceCorrections';
import AdminOverdueSettlements from './pages/admin/OverdueSettlements';
import AdminIntegrations from './pages/admin/Integrations';
import MarketAssignmentPage from './pages/admin/MarketAssignment';
import VenuesTightRadius from './pages/admin/VenuesTightRadius';
import AdminSystemHealth from './pages/admin/SystemHealth';  // Iter-224g
import AdminJobsV2 from './pages/admin/JobsV2';
import BriefingCommandCenter from './pages/admin/BriefingCommandCenter';
import AIInsightsPage from './components/AIInsightsTile';
import AgencySubscriptionSuccess from './pages/AgencySubscriptionSuccess';
import AdminShiftsRouter from './pages/admin/AdminShiftsRouter';
import EmailHealth from './pages/admin/EmailHealth';
import EmailLog from './pages/admin/EmailLog';
import GgfAudit from './pages/admin/GgfAudit';
import AdminGgfSettings from './pages/admin/GgfSettings';
import GgfCertification from './pages/talent/GgfCertification';
import GgfLearningCenter from './pages/talent/GgfLearningCenter';
import AdminCreateShift from './pages/admin/AdminCreateShift';
import ShiftLinkRouter from './components/ShiftLinkRouter';
import BookingActivityPage from './pages/admin/BookingActivity';  // Iter-358 (P0-356)
import OperationsDrilldown from './pages/admin/OperationsDrilldown';  // Iter-360e (P0-356)
import SystemDiagnostics from './pages/admin/SystemDiagnostics';  // Iter-363 (P0 SMS INCIDENT)
import FrontendErrors from './pages/admin/FrontendErrors';  // 2026-09-04 — client error beacon triage
import TalentAgreement from './pages/talent/TalentAgreement';  // Round 2 (ICMA)
import AdminContracts from './pages/admin/AdminContracts';  // Round 2 (ICMA admin)
import AdminRatings from './pages/admin/AdminRatings';
import AdminDirectHireJobs from './pages/admin/AdminDirectHireJobs';
import AdminRequirementApprovals from './pages/admin/AdminRequirementApprovals';
import AdminBroadcast from './pages/admin/AdminBroadcast';
import AdminBroadcastTemplates from './pages/admin/AdminBroadcastTemplates';
import TalentCommunications from './pages/admin/TalentCommunications';
import ProfileNudges from './pages/admin/ProfileNudges';  // Smart Reminder Emails
import AdminPhoneFixer from './pages/admin/AdminPhoneFixer';
import AdminPayouts from './pages/admin/AdminPayouts';
import AdminFailedPayouts from './pages/admin/AdminFailedPayouts';  // P0.16 follow-up
import AdminReconciliation from './pages/admin/AdminReconciliation';  // 2026-10 accounting reconciliation
import AdminAutoPayouts from './pages/admin/AutoPayouts';
import AdminClockoutHygiene from './pages/admin/ClockoutHygiene';
import AdminClockInReviews from './pages/admin/ClockInReviews';
import AdminRatingsModeration from './pages/admin/RatingsModeration';
import AdminProfileQualityReport from './pages/admin/ProfileQualityReport';
import AdminAITalent from './pages/admin/AITalent';
import AdminAvatarStudio from './pages/admin/AdminAvatarStudio';
import AdminAICreators from './pages/admin/AICreators';
import AdminCertAccessLog from './pages/admin/CertAccessLog';
import CertReviewQueue from './pages/admin/CertReviewQueue';
import IdvReviewQueue from './pages/admin/IdvReviewQueue';
import IdvRejectionTemplates from './pages/admin/IdvRejectionTemplates';
import AdminClientApprovals from './pages/admin/ClientApprovals';
import ClientPendingApproval from './pages/client/PendingApproval';
import AdminBookings from './pages/admin/AdminBookings';
import TalentProfileRedirect from './components/TalentProfileRedirect';
import CreatorDashboard from './pages/creator/Dashboard';
import CreatorApply from './pages/creator/Apply';
import TalentOneTapApply from './pages/talent/OneTapApply';
import ClientAIAvatarDetail from './pages/client/AIAvatarDetail';
import MyAvatars from './pages/client/MyAvatars';
import AvatarStudio from './pages/client/AvatarStudio';
import ClientRecapDashboard from './pages/client/RecapDashboard';
import EventChannelDetail from './pages/messaging/EventChannelDetail';
import RootRedirect from './components/RootRedirect';
import TalentSupport from './pages/talent/Support';
import AdminPaymentFlow from './pages/admin/AdminPaymentFlow';
import TalentApproval from './pages/admin/TalentApproval';
import AdminTalentPreview from './pages/admin/AdminTalentPreview';
import RateConfigManager from './pages/admin/RateConfigManager';
import AnalyticsDashboard from './pages/admin/AnalyticsDashboard';
import OperationsDashboard from './pages/admin/OperationsDashboard';
// Iter-248 — Live Event Command Center
import LiveCommandCenter from './pages/admin/LiveCommandCenter';
import EventCloseoutWizard from './pages/admin/EventCloseoutWizard';
import EventOperationsDashboard from './pages/admin/EventOperationsDashboard';
import IncompleteOnboarding from './pages/admin/IncompleteOnboarding';  // Iter-220m
import VerificationFunnel from './pages/admin/VerificationFunnel';  // Iter-315l
import MultiSlotEvents from './pages/admin/MultiSlotEvents';  // Iter-220n
import HelpDeskAdmin from './pages/admin/HelpDeskAdmin';
import CSVImport from './pages/admin/CSVImport';
import AdminAIBookings from './pages/admin/AIBookings';
import AdminCustomRequests from './pages/admin/CustomRequests';
import AIAmbassadorAnalytics from './pages/admin/AIAmbassadorAnalytics';
import CommunityFeedModeration from './pages/admin/CommunityModeration';
import EventHighlightsModeration from './pages/admin/EventHighlightsModeration';
import AdminManagement from './pages/admin/AdminManagement';
import AdminUsers from './pages/admin/Users';
import GalleryApprovals from './pages/admin/GalleryApprovals';
import MergeReview from './pages/admin/MergeReview';
import AdminVideoSettings from './pages/admin/VideoSettings';
import AdminActivityLog from './pages/admin/ActivityLog';
import AdminCalendar from './pages/admin/Calendar';

// Shared pages
import HelpDesk from './pages/HelpDesk';
import AmbassadorProfile from './pages/AmbassadorProfile';
import AmbassadorDiscovery from './pages/AmbassadorDiscovery';

// Agency pages
import AgencyDashboard from './pages/agency/AgencyDashboard';
import AgencyCrm from './pages/agency/AgencyCrm';  // Agency private CRM
import AgencyTeam from './pages/agency/AgencyTeam';  // Agency staff seats
import AgencyTransactions from './pages/agency/AgencyTransactions';  // Agency transaction management
import AcceptAgencyInvite from './pages/agency/AcceptAgencyInvite';
import AgencyQuoteView from './pages/AgencyQuoteView';  // Public quote accept/decline
import AgencyContractSign from './pages/AgencyContractSign';  // Public e-signature
import AgencyInvoicePay from './pages/AgencyInvoicePay';  // Public Stripe invoice payment
import { AgencyWorkspaceRoute } from './components/AgencyWorkspaceRoute';

// Client pages
import ClientHome from './pages/client/Home';
import InfluencerDiscovery from './pages/client/InfluencerDiscovery';
import InfluencerDiscoveryV2 from './pages/client/InfluencerDiscoveryV2';
import PackagesMarketplace from './pages/client/PackagesMarketplace';
import PackageDetail from './pages/client/PackageDetail';
import PackageCheckoutSuccess from './pages/client/PackageCheckoutSuccess';
import ClientPackageOrders from './pages/client/ClientPackageOrders';
import AdminPackages from './pages/admin/AdminPackages';
import CustomAmbassadorRequest from './pages/client/CustomAmbassadorRequest';
import AmbassadorBookingWizard from './pages/client/AmbassadorBookingWizard';
import AmbassadorBookingDetails from './pages/client/AmbassadorBookingDetails';
import AmbassadorBookingsList from './pages/client/AmbassadorBookingsList';


// Talent pages (additional)
import AgencyJobs from './pages/talent/AgencyJobs';
import ProfileBoost from './pages/talent/ProfileBoost';
import InstantPayout from './pages/talent/InstantPayout';
import TalentPortfolio from './pages/talent/Portfolio';
import TalentCreatorProfile from './pages/talent/CreatorProfile';
import TalentCreatorNetwork from './pages/talent/CreatorNetwork';
import AdminCreatorApplications from './pages/admin/CreatorApplications';
import NotificationSettings from './pages/talent/NotificationSettings';
import ApplicationTracker from './pages/talent/ApplicationTracker';
import TalentCheckIn from './pages/talent/CheckIn';
import TalentLeadCapture from './pages/talent/LeadCapture';   // V412 — Client Lead Capture (GGF/Zapier)
import TalentQRScan from './pages/talent/TalentQRScan';   // Iter-291
import EventQRManager from './pages/EventQRManager';      // Iter-291
import ShiftLeadDashboard from './pages/talent/ShiftLeadDashboard';
import GigRolesEditor from './pages/talent/GigRolesEditor';
import CertificationsPage from './pages/talent/Certifications';
import ProfileInfo from './pages/talent/ProfileInfo';
import TalentOnboarding from './pages/talent/Onboarding';  // iter431 — ordered onboarding hub
import TalentOnboardingCompliance from './pages/admin/TalentOnboardingCompliance';  // iter431 — admin compliance
import TalentPremium from './pages/talent/Premium';
import ClockInOut from './pages/talent/ClockInOut';
import TalentReferrals from './pages/talent/Referrals';
import TalentBonusTracker from './pages/talent/BonusTracker';
import TalentScorecard from './pages/talent/Scorecard';

// Client additional pages
import ClientReferrals from './pages/client/Referrals';import ClientSavingsTracker from './pages/client/SavingsTracker';
import AdminRoleManagement from './pages/admin/AdminRoleManagement';
import ReferralEntry from './pages/ReferralEntry';
import ClientTeams from './pages/client/Teams';

// Shared pages
import ContractsPage from './pages/shared/ContractsPage';
import ChatPage from './pages/shared/ChatPage';
import QuizManagement from './pages/shared/QuizManagement';

// Talent pages
import TalentQuizzes from './pages/talent/Quizzes';

// Iter-221 — Settings hubs (talent / client / agency)
import TalentSettings from './pages/settings/TalentSettings';
import ClientSettings from './pages/settings/ClientSettings';
import PublicRoster from './pages/PublicRoster';
import AgencySettings from './pages/settings/AgencySettings';
import ReauthModal from './components/ReauthModal';
import { GlobalPoweredByFooter } from './components/PoweredByFooter';  // Iter-284i — global brand footer on public routes

import GlobalNav from './components/GlobalNav';

function App() {
  // Register service worker on app load
  useEffect(() => {
    registerServiceWorker();
    // Iter-281 P0 — wipe legacy localStorage drafts (pre-fix, unscoped)
    // so they can never bleed across talent profiles in admin/multi-user
    // browsing sessions. Idempotent.
    purgeLegacyDrafts();
    // Iter-411s — App-boot freshness check. Detects a build_id mismatch
    // between the persisted last-boot build and the live backend build.
    // On mismatch it purges legacy auth keys (selectively — never
    // touches token/user) and force-reloads ONCE so a stale PWA
    // runtime cannot boot an obsolete auth lifecycle after an update.
    // Non-blocking on /api/version failure.
    ensureFreshRuntimeAtBoot({ scope: 'app-boot' }).catch(() => { /* never block boot */ });
    // 2026-08-31 — flush photos queued while offline (venue dead zones).
    initOfflineUploadQueue();

    // 2026-09-04 — global client error capture. Any uncaught error or
    // unhandled promise rejection self-reports to the admin Frontend
    // Errors feed (throttled + de-duped in errorBeacon). Turns silent
    // white-screens into visible, triageable signals.
    const onError = (ev) => {
      const src = [ev?.filename, ev?.error?.stack].filter(Boolean).join(' ');
      if (isThirdPartyErrorSource(src)) return;  // extension / injected script noise
      reportFrontendError({
        context: 'window_error',
        message: ev?.message || 'Uncaught error',
        detail: [ev?.filename, ev?.lineno, ev?.colno, ev?.error?.stack]
          .filter(Boolean).join(' · ').slice(0, 4000),
      });
    };
    const onRejection = (ev) => {
      const r = ev?.reason;
      const stack = (r && r.stack) ? r.stack : '';
      if (isThirdPartyErrorSource(stack)) return;  // extension / injected script noise
      reportFrontendError({
        context: 'unhandled_rejection',
        message: (r && (r.message || String(r))) || 'Unhandled promise rejection',
        detail: stack.slice(0, 4000),
      });
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return (
    <AuthProvider>
      <ThemeProvider>
      <BrowserRouter>
        <TalentProfileModalProvider>
        <InviteTalentToShiftProvider>
        <InviteCartProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/roster/:token" element={<ErrorBoundary><PublicRoster /></ErrorBoundary>} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/book/:slug" element={<ErrorBoundary><PublicBooking /></ErrorBoundary>} />
          <Route path="/jobs/:shiftId" element={<ErrorBoundary><PublicJobShare /></ErrorBoundary>} />{/* Iter-290b — public share landing */}
          <Route path="/c/:slug" element={<ErrorBoundary><PublicCompanyHub /></ErrorBoundary>} />{/* Iter-290b Sprint E — company recruiting hub */}
          <Route path="/client/recruiting-toolkit" element={<ProtectedRoute><RecruitingToolkit /></ProtectedRoute>} />{/* Iter-290c (#4) */}
          {/* Iter-228 — Branded agency recruitment landing page (public) */}
          <Route path="/agency/:slug" element={<ErrorBoundary><AgencyRecruitmentPage /></ErrorBoundary>} />
          <Route path="/auth/magic/:token" element={<ErrorBoundary><MagicLogin /></ErrorBoundary>} />
          <Route path="/pay/:token" element={<ErrorBoundary><PayInvoice /></ErrorBoundary>} />{/* Iter-327 — Universal invoice payment link (guest checkout) */}
          <Route path="/agency-quote/:token" element={<ErrorBoundary><AgencyQuoteView /></ErrorBoundary>} />{/* Agency quote accept/decline */}
          <Route path="/agency-sign/:token" element={<ErrorBoundary><AgencyContractSign /></ErrorBoundary>} />{/* Agency contract e-signature */}
          <Route path="/agency-invoice/:token" element={<ErrorBoundary><AgencyInvoicePay /></ErrorBoundary>} />{/* Agency invoice payment */}
          <Route path="/marketplace" element={<ErrorBoundary><Marketplace /></ErrorBoundary>} />{/* Iter-334 */}
          <Route path="/vendor/:slug" element={<ErrorBoundary><VendorPublicPage /></ErrorBoundary>} />{/* Iter-334 */}
          <Route path="/booking/:id" element={<ErrorBoundary><BookingConfirmation /></ErrorBoundary>} />{/* Iter-334 Phase A */}
          <Route path="/s/:shortSlug" element={<ErrorBoundary><ShareLinkResolver /></ErrorBoundary>} />{/* Iter-334 Phase A */}
          <Route path="/vendor-dashboard/marketplace" element={<ProtectedRoute><VendorDashboardMarketplace /></ProtectedRoute>} />{/* Iter-334 Phase A */}
          <Route path="/admin/marketplace/vendors" element={<ProtectedRoute allowedRoles={['admin','super_admin']}><AdminMarketplaceVendors /></ProtectedRoute>} />{/* Iter-334 Phase A */}
          <Route path="/admin/role-management" element={<ProtectedRoute allowedRoles={['admin','super_admin']}><AdminRoleManagement /></ProtectedRoute>} />{/* 8 Package Tiers — staffing directory */}
          <Route path="/admin/talent-protection" element={<ProtectedRoute allowedRoles={['admin','super_admin']}><TalentDbProtection /></ProtectedRoute>} />{/* Critical #1 — Anti-scraping */}
          <Route path="/admin/talent-onboarding" element={<ProtectedRoute allowedRoles={['admin','super_admin']}><TalentOnboardingCompliance /></ProtectedRoute>} />{/* iter431 — onboarding compliance */}
          <Route path="/admin/payment-holds" element={<ProtectedRoute allowedRoles={['admin','super_admin']}><PaymentHoldAdmin /></ProtectedRoute>} />{/* iter433 — payment-hold admin */}
          <Route path="/admin/code-of-conduct" element={<ProtectedRoute allowedRoles={['admin','super_admin']}><AdminCodeOfConduct /></ProtectedRoute>} />{/* 2026-09 — Gig Code of Conduct editor */}
          <Route path="/admin/code-of-conduct/acceptances" element={<ProtectedRoute allowedRoles={['admin','super_admin']}><AdminCoCAcceptances /></ProtectedRoute>} />{/* 2026-09 — CoC acceptance audit */}
          <Route path="/admin/client-concerns" element={<ProtectedRoute allowedRoles={['admin','super_admin']}><AdminClientConcerns /></ProtectedRoute>} />{/* 2026-09 — Client concern review queue */}
          <Route path="/admin/client-talent-blocks" element={<ProtectedRoute allowedRoles={['admin','super_admin']}><ClientTalentBlocks /></ProtectedRoute>} />{/* 2026-06 — Client Block Talent */}
          <Route path="/admin/vendors" element={<ProtectedRoute allowedRoles={['admin','super_admin']}><AdminVendorQueue /></ProtectedRoute>} />{/* Phase 2 vendor foundation */}
          <Route path="/admin/vendor-migration" element={<ProtectedRoute allowedRoles={['admin','super_admin']}><AdminVendorMigration /></ProtectedRoute>} />{/* Busy Woo migration UI */}
          <Route path="/admin/zapier"           element={<ProtectedRoute allowedRoles={['super_admin']}><ZapierIntegrations /></ProtectedRoute>} />{/* Zapier Integration Sprint · Phase 1 */}
          <Route path="/admin/vendor-promo"   element={<ProtectedRoute allowedRoles={['admin','super_admin']}><VendorPromoAdmin /></ProtectedRoute>} />{/* Iter-373/374 launch promo admin */}
          <Route path="/admin/ambassador-media-audit" element={<ProtectedRoute allowedRoles={['admin','super_admin']}><AmbassadorMediaAudit /></ProtectedRoute>} />{/* AI ambassador showcase media audit */}
          <Route path="/admin/event-history" element={<ProtectedRoute allowedRoles={['admin','super_admin']}><EventHistory /></ProtectedRoute>} />{/* Historical event calendar + recovery */}
          <Route path="/admin/staff-payments" element={<ProtectedRoute allowedRoles={['admin','super_admin']}><StaffPayments /></ProtectedRoute>} />{/* Outstanding staff settlements */}
          <Route path="/admin/evie-calls" element={<ProtectedRoute allowedRoles={['admin','super_admin']}><EvieCalls /></ProtectedRoute>} />{/* Evie AI Telephone Receptionist */}
          <Route path="/admin/client-booking-defaults" element={<ProtectedRoute allowedRoles={['admin','super_admin']}><ClientBookingDefaults /></ProtectedRoute>} />{/* Per-client booking defaults */}
          <Route path="/admin/health" element={<ProtectedRoute allowedRoles={['admin','super_admin']}><AdminHealthDashboard /></ProtectedRoute>} />{/* Iter-339 */}
          <Route path="/admin/discrepancies" element={<ProtectedRoute allowedRoles={['admin','super_admin']}><AdminDiscrepancies /></ProtectedRoute>} />{/* Iter-346 */}
          <Route path="/register" element={<Register />} />
          <Route path="/sms-terms" element={<SmsTermsPage />} />
          <Route path="/sms-consent" element={<SmsConsentPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/data-deletion" element={<DataDeletionPage />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
          <Route path="/terms" element={<TermsAndConditionsPage />} />
          <Route path="/admin/companies/:companyId/signers" element={<AdminCompanySignersPage />} />
          {/* Aliases — keep external referral / marketing links working.
              Anyone visiting /signup?ref=CODE or /sign-up?ref=CODE lands on
              the same Register screen with the referral query param intact
              (React Router preserves `?` automatically). */}
          <Route path="/signup" element={<Register />} />
          <Route path="/sign-up" element={<Register />} />
          <Route path="/join" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/activate" element={<Activate />} />   {/* Iter-262b */}
          <Route path="/verify-contact" element={<VerifyContact />} />
          <Route path="/verify-phone" element={<VerifyPhone />} />   {/* iter429 — universal SMS phone verification gate */}
          <Route path="/set-password" element={<SetPassword />} />

          {/* Iter-273 — Email footer destinations. PUBLIC by design so users
              can manage preferences / unsubscribe straight from an email
              without needing to be logged in first. */}
          <Route path="/notifications/preferences" element={<NotificationPreferencesRouter />} />
          <Route path="/notifications/unsubscribe" element={<NotificationUnsubscribePage />} />

          {/* Iter-209 — Client pending approval branded screen.
              Requires login but bypasses GlobalNav + role gates. */}
          <Route path="/client/pending" element={<ClientPendingApproval />} />

          {/* Client routes */}
          <Route
            path="/client/home"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/dashboard"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/shifts"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientShifts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/gigs"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientShifts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/shifts/create"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <CreateShift />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/book-with-evie"
            element={
              <ProtectedRoute allowedRoles={['client', 'admin', 'super_admin']}>
                <EvieBooking />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/shifts/:shiftId"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ShiftDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/calendar"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientCalendar />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/bookings"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientBookings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/influencers"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <InfluencerDiscoveryV2 />
              </ProtectedRoute>
            }
          />
          {/* Iter-234c — alias for older bookmarks / docs that link to /client/marketplace */}
          <Route
            path="/client/marketplace"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <InfluencerDiscoveryV2 />
              </ProtectedRoute>
            }
          />
          {/* Iter-247 — Creator Network alias for the unified Influencer + Content Creator surface.
              Iter-256 — Opened to admin + talent so the Community Feed "View All Creator
              Spotlights" link doesn't forcibly bounce non-client roles. */}
          <Route
            path="/client/creators"
            element={
              <ProtectedRoute allowedRoles={['client', 'admin', 'talent']}>
                <InfluencerDiscoveryV2 />
              </ProtectedRoute>
            }
          />
          {/* Sprint 233 — Creator Marketplace Packages */}
          <Route
            path="/client/packages"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <PackagesMarketplace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/packages/orders"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientPackageOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/packages/checkout/success"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <PackageCheckoutSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/packages/:slug"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <PackageDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/packages"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminPackages />
              </ProtectedRoute>
            }
          />
          {/* Campaign Dashboard removed — redundant with My Jobs/Gigs */}
          <Route
            path="/client/favorites"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientFavorites />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/calendar/integrations"
            element={
              <ProtectedRoute allowedRoles={['client', 'agency', 'admin', 'super_admin']}>
                <ClientCalendarIntegrations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/calendar/review"
            element={
              <ProtectedRoute allowedRoles={['client', 'agency', 'admin', 'super_admin']}>
                <ClientCalendarReviewQueue />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/calendar/sync-logs"
            element={
              <ProtectedRoute allowedRoles={['client', 'agency', 'admin', 'super_admin']}>
                <ClientCalendarSyncLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/ratings"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientRatings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/profile"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/invoices"
            element={
              <ProtectedRoute allowedRoles={['client', 'admin', 'super_admin']}>
                <ClientInvoices />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/invoices/:invoiceId"
            element={
              <ProtectedRoute allowedRoles={['client', 'admin', 'super_admin']}>
                <ClientInvoices />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/messages"
            element={
              <ProtectedRoute allowedRoles={['client', 'agency']}>
                <ClientMessages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/messages/support"
            element={
              <ProtectedRoute allowedRoles={['client', 'agency']}>
                <AdminSupportChat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/payment-hold"
            element={
              <ProtectedRoute allowedRoles={['client', 'agency']}>
                <PaymentHoldScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/messages/evie"
            element={
              <ProtectedRoute allowedRoles={['client', 'agency']}>
                <EvieAssistant />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/blocked-talent"
            element={
              <ProtectedRoute allowedRoles={['client', 'agency']}>
                <BlockedTalent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/messages/:threadId"
            element={
              <ProtectedRoute allowedRoles={['client', 'agency']}>
                <ClientChatThread />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/discover"
            element={
              <ProtectedRoute allowedRoles={['client', 'admin']}>
                <TalentDiscovery />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/discover/:talentId"
            element={
              <ProtectedRoute allowedRoles={['client', 'admin', 'talent']}>
                <TalentProfileDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/messages/talent/:talentId"
            element={
              <ProtectedRoute allowedRoles={['client', 'agency']}>
                <DiscoveryMessage />
              </ProtectedRoute>
            }
          />
          {/* Iter-253 — Creator Network self-preview: shows the talent the
              exact public profile a brand sees. Reuses TalentProfileDetail. */}
          <Route
            path="/talent/me/preview-creator"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentProfileDetail />
              </ProtectedRoute>
            }
          />
          {/* Iter-252 — alias for any legacy /client/talent/{id} deep-links
              (old AI recommendations, emails, JobOverview ribbon). */}
          <Route
            path="/client/talent/:talentId"
            element={
              <ProtectedRoute allowedRoles={['client', 'admin']}>
                <TalentProfileDetail />
              </ProtectedRoute>
            }
          />
          {/* Iter-253d — alias for any legacy /talent/profile/{id} links
              (Creator Spotlight strip, old emails, social-share bookmarks). */}
          <Route
            path="/talent/profile/:talentId"
            element={
              <ProtectedRoute allowedRoles={['client', 'admin', 'talent']}>
                <TalentProfileDetail />
              </ProtectedRoute>
            }
          />
          {/* Community Feed @mention target — privacy-safe member profile,
              open to ALL feed participants (talent/agency/client/admin). */}
          <Route
            path="/community/member/:userId"
            element={
              <ProtectedRoute allowedRoles={['client', 'agency', 'admin', 'super_admin', 'talent']}>
                <CommunityMemberProfile />
              </ProtectedRoute>
            }
          />

          {/* Iter-254 — Dedicated Creator Network profile view. Surfaces
              creator-specific signal (intro reel, follower stats per
              platform, content categories, audience, brand collabs,
              media kit). Non-creators auto-redirect to the generic
              /client/discover detail page. */}
          <Route
            path="/client/creators/:talentId"
            element={
              <ProtectedRoute allowedRoles={['client', 'admin', 'talent']}>
                <CreatorProfileView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/job-overview/:shiftId"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <JobOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/attendance"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <AttendanceDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/reports"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/agreement"
            element={
              <ProtectedRoute allowedRoles={['client', 'agency']}>
                <ClientAgreement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/booking-management"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <BookingManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/settings/subscription"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientSubscription />
              </ProtectedRoute>
            }
          />
          <Route
            path="/referrals"
            element={
              <ProtectedRoute>
                <ReferralEntry />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/referrals"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientReferrals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/savings-tracker"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientSavingsTracker />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/series/create"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <CreateEventSeries />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/teams"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientTeams />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/custom-ambassador"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <CustomAmbassadorRequest />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/book-ambassador/:id"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <AmbassadorBookingWizard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/ambassador-booking/:bookingId"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <AmbassadorBookingDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/ambassador-bookings"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <AmbassadorBookingsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/avatars"
            element={
              <ProtectedRoute allowedRoles={['client', 'agency']}>
                <MyAvatars />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/avatar-studio/:avatarId"
            element={
              <ProtectedRoute allowedRoles={['client', 'agency']}>
                <AvatarStudio />
              </ProtectedRoute>
            }
          />

          {/* AI Ambassador routes (public + protected) */}
          <Route path="/ambassadors" element={<AmbassadorDiscovery />} />
          <Route path="/ambassador/:slug" element={<AmbassadorProfile />} />

          {/* Talent routes */}
          <Route
            path="/talent/home"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/my-card"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentMyCard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/training"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TrainingHub />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/training/category/:categorySlug"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TrainingCategory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/training/module/:moduleId"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TrainingModule />
              </ProtectedRoute>
            }
          />
          {/* Iter-225d Phase 1C — plural alias so backend-emitted assessment_url
              ('/talent/training/modules/{id}') resolves to the same TrainingModule
              page. Keeps URL convention consistent with the rest of the platform. */}
          <Route
            path="/talent/training/modules/:moduleId"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TrainingModule />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/dashboard"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/agreement"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentAgreement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/contracts"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminContracts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/shifts"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <ErrorBoundary fallbackHref="/talent/home" fallbackLabel="Back to Home">
                  <TalentShifts />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          {/* Gig terminology alias */}
          <Route
            path="/talent/gigs"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <ErrorBoundary fallbackHref="/talent/home" fallbackLabel="Back to Home">
                  <TalentShifts />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          {/* Iter-157a — Notification deep-link landing page. Backs the
              URLs embedded in admin_invite SMS/email ("Apply: .../talent/
              shifts/{id}") AND the in-app Notification Center click
              handler. Previously these routes didn't exist → white
              screen. Renders a friendly "opportunity no longer
              available" fallback on 404 instead of blanking out.
              Iter-196 — wrapped in ErrorBoundary so a malformed gig
              payload can never white-screen the apply flow. */}
          <Route
            path="/talent/shifts/:shiftId"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <ErrorBoundary fallbackHref="/talent/shifts" fallbackLabel="Browse gigs">
                  <TalentShiftDetail />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/gigs/:shiftId"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <ErrorBoundary fallbackHref="/talent/shifts" fallbackLabel="Browse gigs">
                  <TalentShiftDetail />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/applications"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <MyApplications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/bookings"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <BookingsRouter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/bookings-v2"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentBookingsV2 />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/booking/:id"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <BookingDetailsV2 />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/clock"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <ClockInOut />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/post-event/:bookingId"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentPostEvent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/earnings"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentSettlements />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/1099"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TaxCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/1099"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <TaxAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/payouts/return"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <PayoutsReturn mode="return" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/payouts/refresh"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <PayoutsReturn mode="refresh" />
              </ProtectedRoute>
            }
          />
          {/* Settlement terminology alias */}
          <Route
            path="/talent/settlements"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentSettlements />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/contracts"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <ContractsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/verify"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <IDVerification />
              </ProtectedRoute>
            }
          />
          {/* Iter-220o — IDV deep-link aliases. The rejection email and any
              external links may reference /talent/idv or /talent/id-verification;
              both must land on the same upload page so users don't dead-end. */}
          <Route
            path="/talent/idv"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <IDVerification />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/id-verification"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <IDVerification />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/notifications"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <NotificationCenter />
              </ProtectedRoute>
            }
          />
          {/* Iter-268 — Talent Messages Center (persistent, actionable inbox) */}
          <Route
            path="/talent/messages-center"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentMessagesCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/notifications"
            element={
              <ProtectedRoute allowedRoles={['client', 'agency']}>
                <Notifications backHref="/client/home" title="Notifications" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/notifications"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <Notifications backHref="/admin" title="Notifications" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/notification-settings"
            element={
              <ProtectedRoute allowedRoles={['talent', 'client', 'agency', 'admin', 'super_admin']}>
                <NotificationSettings />
              </ProtectedRoute>
            }
          />
          {/* Iter-245 — Admin push enable path. Reuses talent notification
              settings (same Web-Push card) so admins/clients can finally
              subscribe to push. */}
          <Route
            path="/admin/notification-settings"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <NotificationSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/notification-settings"
            element={
              <ProtectedRoute allowedRoles={['client', 'agency']}>
                <NotificationSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/ratings"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentRatings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/agency-jobs"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <AgencyJobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/profile-boost"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <ProfileBoost />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/instant-payout"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <InstantPayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/portfolio"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentPortfolio />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/creator-profile"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentCreatorProfile />
              </ProtectedRoute>
            }
          />
          {/* Iter-253 — Creator Network surface (status-aware: apply / pending / approved) */}
          <Route
            path="/talent/creator-network"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentCreatorNetwork />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/creator-applications"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminCreatorApplications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/my-applications"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <MyApplications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/checkin/:bookingId"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentCheckIn />
              </ProtectedRoute>
            }
          />
          {/* V412 — Client Lead Capture (embedded Zapier/etc form during shift) */}
          <Route
            path="/talent/bookings/:bookingId/leads"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentLeadCapture />
              </ProtectedRoute>
            }
          />
          {/* Iter-291 — Talent QR scan landing */}
          <Route
            path="/talent/qr-scan"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentQRScan />
              </ProtectedRoute>
            }
          />
          {/* Iter-291 — Event QR Manager (admin + client + agency owners) */}
          <Route
            path="/shifts/:shiftId/qr"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin', 'client', 'agency']}>
                <EventQRManager />
              </ProtectedRoute>
            }
          />
          {/* Iter-156f — Add to Calendar emails (pre-shift reminders +
              shift-update notices) deep-link to /talent/bookings/{id}
              as the canonical "view this booking" URL. Alias to the
              same TalentCheckIn page so external links don't 404. */}
          <Route
            path="/talent/bookings/:bookingId"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentCheckIn />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/shift-lead/:shiftId"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <ShiftLeadDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/gig-roles"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <GigRolesEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/certifications"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <CertificationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/profile-info"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <ProfileInfo />
              </ProtectedRoute>
            }
          />
          {/* Alias: legacy /talent/profile and /talent/profile-complete → ProfileInfo */}
          <Route
            path="/talent/profile"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <ProfileInfo />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/profile-complete"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <ProfileInfo />
              </ProtectedRoute>
            }
          />
          {/* Iter-226g — Talent availability calendar */}
          <Route
            path="/talent/availability"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentAvailability />
              </ProtectedRoute>
            }
          />
          {/* Iter-227a — Talent invitations inbox with decline-with-block */}
          <Route
            path="/talent/invitations"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <MyInvitations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/helpdesk"
            element={
              <ProtectedRoute allowedRoles={['talent', 'client', 'admin', 'super_admin']}>
                <HelpDesk />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/premium"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentPremium />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/premium/success"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentPremium />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/premium/cancel"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentPremium />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/settings/subscription"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentPremium />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/referrals"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentReferrals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/bonus-tracker"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentBonusTracker />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/scorecard"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentScorecard />
              </ProtectedRoute>
            }
          />

          {/* Agency routes */}
          <Route
            path="/agency/dashboard"
            element={
              <ProtectedRoute allowedRoles={['agency']}>
                <AgencyDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agency/crm"
            element={
              <AgencyWorkspaceRoute>
                <AgencyCrm />
              </AgencyWorkspaceRoute>
            }
          />
          <Route
            path="/agency/crm/tasks"
            element={
              <AgencyWorkspaceRoute>
                <AgencyCrm />
              </AgencyWorkspaceRoute>
            }
          />
          <Route
            path="/agency/team"
            element={
              <AgencyWorkspaceRoute>
                <AgencyTeam />
              </AgencyWorkspaceRoute>
            }
          />
          <Route
            path="/agency/transactions"
            element={
              <AgencyWorkspaceRoute>
                <AgencyTransactions />
              </AgencyWorkspaceRoute>
            }
          />
          <Route path="/agency/invite/accept" element={<AcceptAgencyInvite />} />
          {/* Iter-228 — Agency recruitment workspace */}
          <Route
            path="/agency/recruitment"
            element={
              <ProtectedRoute allowedRoles={['agency']}>
                <AgencyRecruitmentSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agency/roster"
            element={
              <ProtectedRoute allowedRoles={['agency']}>
                <AgencyRoster />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agency/talent-hub"
            element={
              <ProtectedRoute allowedRoles={['agency']}>
                <AgencyRoster />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agency/talent-pool"
            element={
              <ProtectedRoute allowedRoles={['agency']}>
                <AgencyRoster />
              </ProtectedRoute>
            }
          />

          {/* Client contracts */}
          <Route
            path="/client/contracts"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ContractsPage />
              </ProtectedRoute>
            }
          />

          {/* Admin routes */}
          {/* Iter-162a — alias `/admin` to `/admin/home` so back navigation
              never lands on a 404. */}
          <Route path="/admin" element={<Navigate to="/admin/home" replace />} />
          <Route
            path="/admin/home"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminHomeRouter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/smoke-test"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminSmokeTest />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/frontend-errors"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <FrontendErrors />
              </ProtectedRoute>
            }
          />
          {/* Iter-311b — Admin CRUD console for client_business_rules */}
          <Route
            path="/admin/client-business-rules"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <ClientBusinessRulesConsole />
              </ProtectedRoute>
            }
          />
          {/* Iter-233d — fix "Page not found" on Admin Messages */}
          <Route
            path="/admin/messages/dm/:talentId"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminDirectMessage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/messages"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminMessages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/flagged-messages"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <FlaggedMessages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/flagged-images"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <FlaggedProfileImages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ai-support"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AiEscalations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminMessages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/team-members"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientTeamMembers />
              </ProtectedRoute>
            }
          />
          {/* Iter-236 — Campaign Intelligence Phase 2 */}
          <Route
            path="/client/insights"
            element={
              <ProtectedRoute allowedRoles={['client','admin','super_admin']}>
                <ClientInsightsRollup />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/events/:eventKey/insights"
            element={
              <ProtectedRoute allowedRoles={['client','admin','super_admin']}>
                <EventInsightsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/insights/clients/:clientId"
            element={
              <ProtectedRoute allowedRoles={['admin','super_admin']}>
                <AdminClientInsights />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/events/:eventKey/insights"
            element={
              <ProtectedRoute allowedRoles={['admin','super_admin']}>
                <EventInsightsDashboard />
              </ProtectedRoute>
            }
          />
          {/* Iter-237 — Campaign Intelligence Phase 3 */}
          <Route
            path="/talent/affiliate"
            element={
              <ProtectedRoute allowedRoles={['talent','admin','super_admin']}>
                <CreatorAffiliateDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/settings/distribution-lists"
            element={
              <ProtectedRoute allowedRoles={['client','admin','super_admin']}>
                <DistributionLists />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/revenue"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminRevenue />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/shift-metrics-preview"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminShiftMetricsPreview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/shift-metrics"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminShiftMetricsReview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/referral-leaderboard"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <ReferralLeaderboard />
              </ProtectedRoute>
            }
          />
          {/* Iter-221+ — legacy `/admin/idv` deprecated. Redirect to the
              canonical IDV Review queue so admins never land on the
              old free-text rejection page. */}
          <Route
            path="/admin/idv"
            element={<Navigate to="/admin/idv-review" replace />}
          />
          <Route
            path="/admin/contracts"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <ContractManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/profile"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminProfile />
              </ProtectedRoute>
            }
          />
          {/* Iter-246b — Admin Settings hub. Configuration only — operational
              tools (IDV review, GGF audit, recap compliance) live in
              Operations Center per Mel's Iter-246c feedback. */}
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminSettings />
              </ProtectedRoute>
            }
          />
          {/* 2026-08-31 — per-event/per-channel ops notification toggles. */}
          <Route
            path="/admin/notification-preferences"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminNotificationPreferences />
              </ProtectedRoute>
            }
          />
          {/* 2026-06 — Admin Delivery Health (email/SMS/in-app delivery). */}
          <Route
            path="/admin/delivery-health"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin', 'talent_admin', 'finance_admin']}>
                <AdminDeliveryHealth />
              </ProtectedRoute>
            }
          />
          {/* 2026-06 — Admin Settlement Challenges (contractor dispute review). */}
          <Route
            path="/admin/settlement-challenges"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin', 'talent_admin', 'finance_admin']}>
                <AdminSettlementChallenges />
              </ProtectedRoute>
            }
          />
          {/* iter419 — Admin Attendance Corrections (timezone/clock-in review). */}
          <Route
            path="/admin/attendance-corrections"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin', 'talent_admin', 'finance_admin']}>
                <AdminAttendanceCorrections />
              </ProtectedRoute>
            }
          />
          {/* Retired parallel AI-avatar page — redirect to the unified AI Talent catalog. */}
          <Route
            path="/admin/ai-avatars"
            element={<Navigate to="/admin/ai-talent" replace />}
          />
          {/* 2026-06 — Admin Overdue Settlements queue. */}
          <Route
            path="/admin/overdue-settlements"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin', 'talent_admin', 'finance_admin']}>
                <AdminOverdueSettlements />
              </ProtectedRoute>
            }
          />
          {/* Iter-246c — Admin Recap Compliance (Operations Center surface). */}
          <Route
            path="/admin/recaps"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminRecapCompliance />
              </ProtectedRoute>
            }
          />
          {/* 2026-08-30 — canonical `/admin/recap-compliance` route alias. */}
          <Route
            path="/admin/recap-compliance"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminRecapCompliance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/shifts"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminShiftsRouter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/jobs-v2"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminJobsV2 />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/briefing-command"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <BriefingCommandCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ai-insights"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AIInsightsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agency/subscription-success"
            element={
              <ProtectedRoute allowedRoles={['agency']}>
                <AgencySubscriptionSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/system/email-health"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <EmailHealth />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/system/email-log"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <EmailLog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ggf/audit"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <GgfAudit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ggf/settings"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminGgfSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/certifications/ggf"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <GgfCertification />
              </ProtectedRoute>
            }
          />
          {/* Iter-246d — GGF Learning Center: 5-module pre-read before the assessment. */}
          <Route
            path="/talent/certifications/ggf/learn"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <GgfLearningCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/shifts/create"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminCreateShift />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/shifts/:shiftId"
            element={<ShiftLinkRouter />}
          />{/* Role-aware: talent → gig apply page, admin → admin detail */}
          <Route
            path="/admin/shifts/:shiftId/preview-as-talent"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminTalentPreview />
              </ProtectedRoute>
            }
          />{/* Admin preview-as-talent — see exactly what talent sees */}
          <Route
            path="/admin/talent-approval"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <TalentApproval />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ratings"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminRatings />
              </ProtectedRoute>
            }
          />
          {/* Iter-157g — alias route. The QuickActionBar + admin Home
              card both deep-link to /admin/ratings/all (the backend
              endpoint is also /api/admin/ratings/all), but the React
              route was never registered → catch-all NotFound rendered
              instead of the page. Both URLs now mount the same
              component for backward compatibility. */}
          <Route
            path="/admin/ratings/all"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminRatings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/direct-hire-jobs"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminDirectHireJobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/requirement-approvals"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminRequirementApprovals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/broadcast"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminBroadcast />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/broadcast/templates"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminBroadcastTemplates />
              </ProtectedRoute>
            }
          />
          {/* Iter-247b — hyphenated alias (operator-friendly URL) */}
          <Route
            path="/admin/broadcast-templates"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminBroadcastTemplates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/help"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminHelp />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/help"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentHelp />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/help"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientHelp />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/background-checks"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <BackgroundChecksKanban />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/api-keys"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <EnterpriseApiKeys />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/booking-requests"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <BookingRequestsAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/phone-fixer"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminPhoneFixer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/payouts"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminPayouts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/payouts/failed"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminFailedPayouts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reconciliation"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminReconciliation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/auto-payouts"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminAutoPayouts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/clockout-hygiene"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminClockoutHygiene />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/clock-in-reviews"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminClockInReviews />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ratings/moderation"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminRatingsModeration />
              </ProtectedRoute>
            }
          />
          {/* Iter-170 P0 — alias redirects so older/typo links never 404. */}
          <Route path="/admin/moderation"        element={<Navigate to="/admin/ratings/moderation" replace />} />
          <Route path="/admin/reviews/moderation" element={<Navigate to="/admin/ratings/moderation" replace />} />
          <Route path="/admin/ratings/queue"     element={<Navigate to="/admin/ratings/moderation" replace />} />
          <Route
            path="/admin/profile-quality"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminProfileQualityReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ai-talent"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminAITalent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/avatar-studio"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminAvatarStudio />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ai-creators"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminAICreators />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/compliance/cert-access-log"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminCertAccessLog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/certifications"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <CertReviewQueue />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/idv-review"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <IdvReviewQueue />
              </ProtectedRoute>
            }
          />
          {/* Iter-315l — Verification Funnel Dashboard (IDV tracking) */}
          <Route
            path="/admin/verification-funnel"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <VerificationFunnel />
              </ProtectedRoute>
            }
          />
          {/* Iter-258 — VA Action Queue: completed activations missing
              post-event data (leads/engagements/samples/photos/recap). */}
          <Route
            path="/admin/va-queue"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <VAActionQueue />
              </ProtectedRoute>
            }
          />
          {/* Iter-263 — GPS Attendance Verification admin routes */}
          <Route
            path="/admin/attendance-queue"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminAttendanceQueue />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/gps-trail/:bookingId"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminGPSTrail />
              </ProtectedRoute>
            }
          />
          {/* Iter-266 — GPS Phase B reliability scoring */}
          <Route
            path="/admin/reliability"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminReliabilityDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reliability/talent/:talentId"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminTalentReliability />
              </ProtectedRoute>
            }
          />
          {/* Iter-412a — Reliability Beta admin-only validation console */}
          <Route
            path="/admin/reliability/beta"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminReliabilityBeta />
              </ProtectedRoute>
            }
          />
          {/* PO8/PO9 — Compliance engine dashboard (Sprint 2026-08-05) */}
          <Route
            path="/admin/compliance"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminComplianceDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/idv-rejection-templates"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <IdvRejectionTemplates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/client-approvals"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminClientApprovals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/bookings"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminBookings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/dashboard"
            element={
              <ProtectedRoute allowedRoles={['ai_creator']}>
                <CreatorDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/creator/apply" element={<CreatorApply />} />
          <Route
            path="/talent/apply/:shiftId"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentOneTapApply />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/ai-talent/:talentId"
            element={
              <ProtectedRoute allowedRoles={['client', 'admin', 'super_admin']}>
                <ClientAIAvatarDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/recap/:shiftId"
            element={
              <ProtectedRoute allowedRoles={['client', 'admin', 'super_admin']}>
                <ClientRecapDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages/channel/:channelId"
            element={
              <ProtectedRoute allowedRoles={['talent', 'client', 'admin', 'super_admin']}>
                <EventChannelDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/messages"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <Navigate to="/talent/messages-center?tab=channels" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/support"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentSupport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/recap-diag"
            element={
              <ProtectedRoute allowedRoles={['talent', 'admin', 'super_admin']}>
                <RecapDiag />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/messages-center"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientMessagesCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/messages-center"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminMessagesCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/payment-flow"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminPaymentFlow />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/expenses"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminExpenses />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/rate-config"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <RateConfigManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AnalyticsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/operations"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <OperationsDashboard />
              </ProtectedRoute>
            }
          />
          {/* Iter-248 — Live Event Command Center (GPS map + activity + AI alerts) */}
          <Route
            path="/admin/live-ops"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <LiveCommandCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/operations/closeout/:shiftId"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <EventCloseoutWizard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/operations/event/:shiftId"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <EventOperationsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/incomplete-onboarding"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <IncompleteOnboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/multi-slot-events"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <MultiSlotEvents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ai-bookings"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminAIBookings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/custom-requests"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminCustomRequests />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ai-analytics"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AIAmbassadorAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/helpdesk"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <HelpDeskAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/csv-import"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <CSVImport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/community-moderation"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <CommunityFeedModeration />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/live-event-inbox"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <LiveEventInbox />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/event-highlights-moderation"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <EventHighlightsModeration />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/admin-management"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/talent-communications"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <TalentCommunications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/profile-nudges"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <ProfileNudges />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/gallery-approvals"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <GalleryApprovals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/merge-review"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <MergeReview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/markets"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminMarkets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/talent/:userId"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminUserDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/client/:userId"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminUserDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/videos"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminVideoSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/integrations"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminIntegrations />
              </ProtectedRoute>
            }
          />
          {/* Iter-411l — Talent Market Assignment (backfill + review queue) */}
          <Route
            path="/admin/market-assignment"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <MarketAssignmentPage />
              </ProtectedRoute>
            }
          />
          {/* Iter-411m — Venues With Tight Radius report */}
          <Route
            path="/admin/venues-tight-radius"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <VenuesTightRadius />
              </ProtectedRoute>
            }
          />
          {/* Iter-224g — Dedicated System Health diagnostics page */}
          <Route
            path="/admin/system-health"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminSystemHealth />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/video-settings"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminVideoSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/activity-log"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminActivityLog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/calendar"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminCalendar />
              </ProtectedRoute>
            }
          />

          {/* Chat route (shared) */}
          <Route
            path="/chat"
            element={
              <ProtectedRoute allowedRoles={['client', 'talent', 'admin', 'super_admin']}>
                <ChatPage />
              </ProtectedRoute>
            }
          />

          {/* Community Feed (shared — all roles) */}
          <Route
            path="/community"
            element={
              <ProtectedRoute allowedRoles={['client', 'talent', 'admin', 'super_admin', 'talent_admin', 'client_admin', 'finance_admin']}>
                <CommunityFeed />
              </ProtectedRoute>
            }
          />

          {/* Admin Permissions Matrix (super admin) */}
          <Route
            path="/admin/permissions"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminPermissionsMatrix />
              </ProtectedRoute>
            }
          />

          {/* Pending Payment Approvals (admin with can_edit_billing) */}
          <Route
            path="/admin/billing-approvals"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <BillingApprovals />
              </ProtectedRoute>
            }
          />

          {/* Test Data Cleanup Center (super admin only) */}
          <Route
            path="/admin/cleanup"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <CleanupCenter />
              </ProtectedRoute>
            }
          />

          {/* Admin Invoice List + Detail */}
          <Route
            path="/admin/invoices"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminInvoices />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/invoices/:invoiceId"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminInvoices />
              </ProtectedRoute>
            }
          />
          {/* Iter-328 Phase 1 — Native Staff Payments through Stripe Connect */}
          <Route
            path="/admin/staff-payments"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminStaffPayments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/alert-preferences"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AlertPreferences />
              </ProtectedRoute>
            }
          />

          {/* Client company users (invites) */}
          <Route
            path="/client/company"
            element={
              <ProtectedRoute allowedRoles={['client', 'agency']}>
                <CompanyUsers />
              </ProtectedRoute>
            }
          />

          {/* Client billing overview */}
          <Route
            path="/client/billing"
            element={
              <ProtectedRoute allowedRoles={['client', 'agency']}>
                <ClientBilling />
              </ProtectedRoute>
            }
          />

          {/* Public: accept invitation */}
          <Route path="/invite/accept" element={<AcceptInvite />} />
          {/* Iter-133s — Phase C role-based post-event survey */}
          <Route path="/talent/bookings/:bookingId/survey" element={<ErrorBoundary><PostEventSurveyV2 /></ErrorBoundary>} />
          <Route path="/admin/shifts/:shiftId/recap-v2" element={<ErrorBoundary><ShiftRecapV2 audience="admin" /></ErrorBoundary>} />
          <Route path="/client/shifts/:shiftId/recap-v2" element={<ErrorBoundary><ShiftRecapV2 audience="client" /></ErrorBoundary>} />

          {/* Iter-358 (P0-356) — Standalone booking audit trail. */}
          <Route path="/admin/booking/:booking_id/activity"
                 element={<ErrorBoundary><ProtectedRoute allowedRoles={['admin','super_admin']}><BookingActivityPage /></ProtectedRoute></ErrorBoundary>} />

          {/* Iter-360e (P0-356 follow-up) — Operations tile drill-downs. */}
          <Route path="/admin/operations/drilldown/:key"
                 element={<ErrorBoundary><ProtectedRoute allowedRoles={['admin','super_admin']}><OperationsDrilldown /></ProtectedRoute></ErrorBoundary>} />

          {/* Iter-363 (P0 SMS INCIDENT) — Super Admin System Diagnostics UI. */}
          <Route path="/admin/system/diagnostics"
                 element={<ErrorBoundary><ProtectedRoute allowedRoles={['super_admin','admin']}><SystemDiagnostics /></ProtectedRoute></ErrorBoundary>} />

          {/* Quiz routes */}
          <Route
            path="/quizzes/manage"
            element={
              <ProtectedRoute allowedRoles={['client', 'admin', 'super_admin']}>
                <QuizManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/quizzes"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentQuizzes />
              </ProtectedRoute>
            }
          />

          {/* Default redirect — Iter-417: authenticated users go to their
              role home; only guests hit /login. */}
          <Route path="/" element={<RootRedirect />} />

          {/* Iter-182 — Legacy / typo-tolerant route aliases.
              Cached email links and old SMS notifications used these
              singular forms; redirect to the canonical plural routes
              instead of throwing the catch-all NotFound. */}
          <Route path="/admin/shift/:shiftId" element={<LegacyRedirect to="/admin/shifts/:shiftId" />} />
          <Route path="/talent/contract" element={<Navigate to="/talent/contracts" replace />} />
          <Route path="/talent/contractor-agreement" element={<Navigate to="/talent/agreement" replace />} />
          <Route path="/talent/onboarding" element={<ProtectedRoute allowedRoles={['talent']}><TalentOnboarding /></ProtectedRoute>} />{/* iter431 — ordered onboarding hub */}
          <Route path="/client/contract" element={<Navigate to="/client/contracts" replace />} />

          {/* Iter-221 — Settings hubs (Phase 1 scaffold). Role-gated
              shells with shared autosave + re-auth framework. Module
              bodies are placeholder cards in Phase 1; Phase 2 wires in
              profile / payout / billing / team / notifications etc.
              Must be declared BEFORE the /talent/:talentId wildcard
              below so `/talent/settings` doesn't get swallowed. */}
          <Route
            path="/talent/settings"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <TalentSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent/blocked-clients"
            element={
              <ProtectedRoute allowedRoles={['talent']}>
                <BlockedClients />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/settings"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/settings/:section"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agency/settings"
            element={
              <ProtectedRoute allowedRoles={['agency']}>
                <AgencySettings />
              </ProtectedRoute>
            }
          />

          {/* Iter-211 — Legacy `/talent/{uuid}` deep-link guard.
              Old emails / push-notifications / cached client app bundles
              point at /talent/{uuid} for the talent profile page (mobile
              users were hitting "Page not found"). Forward each role to
              their canonical full-profile route. Specific static talent
              routes above (/talent/home, /talent/shifts, etc.) beat this
              wildcard via React Router v6 ranking. */}
          <Route path="/talent/:talentId" element={<TalentProfileRedirect />} />

          {/* Iter-157a — Catch-all NotFound so unknown URLs never render a
              blank white screen (e.g. stale deep-links in old emails).
              Role-agnostic: friendly message + a button back to their
              dashboard based on the JWT role. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Toaster position="top-right" />
        <QueuedPhotosBadge />
        <PWAInstallBanner />
        <NavigationProgress />
        <ImpersonationBanner />
        <ServerWakingBanner />{/* Iter-292e — P0 deploy stability: show during transient backend failures */}
        <AdminGuidedTour />
        {/* Iter-315m — Consolidated first-time popup controller.
            Replaces the standalone <WhatsNewModal /> mount so popups no
            longer stack. WhatsNewModal is rendered by the gate when the
            user has completed IDV (talent) or is a client/admin. */}
        <OnboardingWizardGate />
        {/* Iter-219 — Contract Story 3 — listens to 'agreement-gate' window
            events dispatched by utils/api.js for 409 and X-Agreement-Warning
            header responses. Mobile-friendly Radix dialog. */}
        <AgreementGateModal />
        {/* Iter-221 — Settings hubs re-auth framework. Listens for
            'reauth-required' events dispatched by `useAutosave` (on 401
            for sensitive saves) or any code path that wants to gate an
            action behind password confirmation. Dispatches
            'reauth-success' so the original save can transparently
            retry. */}
        <ReauthModal />
        <InviteCartFloatingBar />
        <InviteCartDrawer />
        {/* Iter-197 — soft outdated-bundle refresh banner. Listens for
            SW_UPDATE_AVAILABLE broadcasts; never reload-bombs users
            mid-apply / mid-checkout (sensitive-route + idle guard). */}
        <UpdateAvailableToast />
        {/* 2026-06-20 — Live-session auto-update detection (SW-independent).
            Polls /api/version on an interval + on tab focus; shows the branded
            "What's New at iEvntür!" prompt and safely auto-reloads when idle. */}
        <AppUpdateManager />
        {/* Iter-284i — Global "Powered by iEvntür" footer. Renders only on
            externally-shared public routes (booking links, agency landing,
            ambassador profile, onboarding, etc.). Path matchers live in
            components/PoweredByFooter.js — adding a route there is the
            single point of maintenance. */}
        <GlobalPoweredByFooter />
        </InviteCartProvider>
        </InviteTalentToShiftProvider>
        </TalentProfileModalProvider>
      </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
